/**
 * Unit conversion and derived pricing.
 *
 * The base unit is canonical: every stored quantity — cart, order, stock,
 * price tiers, minimums — is in base units. A selected unit is a lens applied
 * at two places only, quantity entry and display, and this module is the only
 * place the arithmetic for that lens lives.
 *
 * Pure. No I/O, no framework imports, so every rule here is unit-testable and
 * shared verbatim by the admin, the storefront and the checkout route.
 */

import type { ProductUnitDTO } from "./types";

/** A factor that is absent, zero, negative or not finite means "no conversion". */
function safeFactor(factor: number | null | undefined): number {
  const n = Number(factor);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

/** Money is 2dp. `0.45 * 1000` is 450.00000000000006 without this. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Whole units the customer entered → base units to store. 2 cartons → 2000. */
export function toBaseQty(unitQty: number, factor: number): number {
  return unitQty * safeFactor(factor);
}

/**
 * Base units → whole units to display. 2000 pieces → 2 cartons.
 *
 * Rounds UP. A line holding 1500 pieces shown as cartons is 2, not 1: rounding
 * down could land a switch on zero, and quietly dropping units the customer
 * asked for is worse than offering slightly more.
 */
export function toUnitQty(baseQty: number, factor: number): number {
  return Math.ceil(baseQty / safeFactor(factor));
}

/** The price of one whole unit, derived from the base price. Never stored. */
export function unitPrice(basePrice: number, factor: number): number {
  return round2(basePrice * safeFactor(factor));
}

/**
 * The step, in base units, for the +/- control while this unit is selected.
 * One tap is one carton. The base unit itself has no factor to step by, so it
 * keeps using the product's own `qty_step`.
 */
export function unitStepBase(factor: number, qtyStep: number): number {
  const f = safeFactor(factor);
  if (f > 1) return f;
  const step = Number(qtyStep);
  return Number.isFinite(step) && step >= 1 ? Math.floor(step) : 1;
}

/**
 * The minimum, in base units, while this unit is selected: the product's own
 * minimum raised to the next whole unit. A 100-piece minimum on a product sold
 * by the 1000-piece carton is one carton.
 */
export function unitMinBase(minOrderQtyBase: number, factor: number): number {
  const f = safeFactor(factor);
  const min = Number.isFinite(minOrderQtyBase) ? Math.max(1, minOrderQtyBase) : 1;
  return Math.max(1, Math.ceil(min / f)) * f;
}

/**
 * Snap an arbitrary quantity onto the grid of whole units.
 *
 * Rounds DOWN — a shopper who types 1234 against a 1000-piece carton gets one
 * carton, not two: quietly charging for a carton they did not ask for is worse
 * than giving them less than they typed, and the value they end up with is
 * always one they could have reached with the +/- control.
 *
 * `minBase` is the floor and wins over the rounding, so the result is never
 * below the quantity the product is sold in. Pass a `minBase` that is already
 * a whole multiple (i.e. from `unitMinBase`) and every result is one too.
 *
 * At factor 1 this is exactly `Math.max(qty, minBase)` — the clamp the
 * storefront already applied before selling units existed.
 */
export function snapToWholeUnits(
  qty: number,
  factor: number,
  minBase: number,
): number {
  const f = safeFactor(factor);
  const floor = Number.isFinite(minBase) ? Math.max(1, Math.floor(minBase)) : 1;
  if (!Number.isFinite(qty)) return floor;
  const snapped = Math.floor(Math.max(0, qty) / f) * f;
  return Math.max(snapped, floor);
}

/**
 * Whole units available. Floors, because half a carton cannot be sold.
 * Infinity when stock is untracked — mirroring `availableUnits` in
 * `@/lib/cart/stock`.
 */
export function maxWholeUnits(
  availableBase: number | null | undefined,
  factor: number,
): number {
  if (availableBase === null || availableBase === undefined) return Infinity;
  return Math.floor(Math.max(0, availableBase) / safeFactor(factor));
}

/**
 * The unit a product is quoted in when the customer has not chosen yet.
 * The flagged default wins; otherwise the smallest factor, which is the least
 * surprising thing to show on a card.
 */
export function defaultProductUnit(
  units: ProductUnitDTO[],
): ProductUnitDTO | null {
  if (units.length === 0) return null;
  return (
    units.find((u) => u.is_default) ??
    [...units].sort((a, b) => a.factor - b.factor)[0]
  );
}
